import { useState } from "react";
import type { MissionIoBlock } from "../../utils/missionTrace";
import {
  APP_META,
  formatDateTime,
  formatMs,
  type MissionOrigin,
} from "../../utils/missionTrace";

export type ResolvedUser = {
  displayName?: string | null;
  email?: string | null;
};

function appChip(appId?: string | null): { icon: string; label: string } {
  const key = (appId || "currents").trim().toLowerCase();
  return APP_META[key] ?? { icon: "🧩", label: key };
}

function formatUserLabel(
  origin: MissionOrigin,
  resolvedUser?: ResolvedUser | null,
): string {
  const type = origin.triggerType?.toUpperCase();
  if (type === "CRON" || type === "WEBHOOK" || type === "API") {
    return origin.triggeredBy || "—";
  }
  if (resolvedUser?.displayName && resolvedUser?.email) {
    return `${resolvedUser.displayName} · ${resolvedUser.email}`;
  }
  if (resolvedUser?.displayName) return resolvedUser.displayName;
  if (resolvedUser?.email) return resolvedUser.email;
  return origin.triggeredBy || "—";
}

function ContextSection({ blocks }: { blocks: MissionIoBlock[] }) {
  const [open, setOpen] = useState(false);
  if (blocks.length === 0) {
    return (
      <div className="mission-origin-row">
        <div className="mission-io-label">Contesto</div>
        <p className="muted mission-origin-empty">—</p>
      </div>
    );
  }
  return (
    <div className="mission-origin-row">
      <button
        type="button"
        className="mission-origin-context-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="mission-io-label">Contesto</span>
        <span className="muted">{open ? "▾" : "▸"} {blocks.length} campi</span>
      </button>
      {open && (
        <div className="mission-origin-context-body">
          {blocks.map((b) => (
            <div key={b.label} className="mission-io-block">
              <div className="mission-io-label">{b.label}</div>
              <pre className="mission-io-value">{b.value}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function MissionOriginBanner({
  origin,
  resolvedUser,
  durationMs,
}: {
  origin: MissionOrigin;
  resolvedUser?: ResolvedUser | null;
  durationMs?: number | null;
}) {
  const app = appChip(origin.appId);
  const hasQuestion = Boolean(origin.userQuestion?.trim());
  const hasTimes = origin.startedAt || origin.endedAt;

  if (!hasQuestion && !hasTimes && !origin.appId && !origin.triggeredBy) return null;

  return (
    <section className="mission-origin-banner" aria-label="Origine missione">
      <h3 className="mission-trace-subtitle">Origine</h3>
      <div className="mission-origin-grid">
        <div className="mission-origin-row">
          <div className="mission-io-label">App</div>
          <span className="chip" title={app.label}>
            {app.icon} {app.label}
          </span>
        </div>
        <div className="mission-origin-row">
          <div className="mission-io-label">Utente</div>
          <span className="mission-origin-user">{formatUserLabel(origin, resolvedUser)}</span>
        </div>
        <div className="mission-origin-row">
          <div className="mission-io-label">Domanda</div>
          {hasQuestion ? (
            <pre className="mission-io-value mission-origin-request-text">{origin.userQuestion}</pre>
          ) : (
            <p className="muted mission-origin-empty">—</p>
          )}
        </div>
        <ContextSection blocks={origin.contextBlocks} />
      </div>
      {hasTimes && (
        <div className="mission-origin-meta">
          <span className="chip mission-origin-times" title="Inizio e fine missione">
            🕐 {formatDateTime(origin.startedAt)} → {formatDateTime(origin.endedAt)}
            {durationMs != null && durationMs > 0 && (
              <span className="muted"> ({formatMs(durationMs)})</span>
            )}
          </span>
        </div>
      )}
    </section>
  );
}

export function MissionOriginChip({
  appId,
  userLabel,
  userQuestion,
}: {
  appId?: string | null;
  userLabel?: string | null;
  userQuestion?: string | null;
}) {
  const app = appChip(appId);
  const title = [userLabel, userQuestion?.trim()].filter(Boolean).join(" — ");
  return (
    <span className="chip" title={title || app.label}>
      {app.icon} {app.label}
      {userLabel && (
        <span className="muted">
          {" "}
          · {userLabel.length > 18 ? `${userLabel.slice(0, 16)}…` : userLabel}
        </span>
      )}
    </span>
  );
}
